import { useEvent } from './useEvent';
import { useEffect, useRef, useState } from 'react';

/**
 * Hook for maintaining internal state for numeric inputs to preserve display values
 * (e.g., "1." or "1.50") while only sending complete numbers to the parent.
 *
 * This solves the problem where typing a decimal point gets immediately lost because
 * Number("1.") === 1, which strips the decimal when passed back to the controlled input.
 *
 * @param externalValue - The current numeric value from parent state
 * @param onValueChange - Callback to update the parent state with a complete numeric value
 * @param emptyValue - The value to use when input is empty. Defaults to 0
 * @returns Object with displayValue, handleChange function, and setDisplayValue for direct control
 */
export function useNumericInputState(
    externalValue: number | undefined,
    onValueChange: (value: number) => void,
    emptyValue: number = 0,
) {
    const [displayValue, setDisplayValue] = useState<string>(() =>
        externalValue !== undefined ? String(externalValue) : '',
    );
    const lastSentValue = useRef<number | undefined>(externalValue);

    // Sync display value with external data changes (but not internal changes)
    useEffect(() => {
        if (externalValue !== lastSentValue.current) {
            setDisplayValue(externalValue !== undefined ? String(externalValue) : '');
            lastSentValue.current = externalValue;
        }
    }, [externalValue]);

    const handleChange = useEvent((value: string) => {
        // Update display value immediately for smooth typing
        setDisplayValue(value);

        const trimmedValue = value.trim();
        if (trimmedValue === '') {
            lastSentValue.current = emptyValue;
            onValueChange(emptyValue);
            return;
        }

        // Check if this is an incomplete number that the user is still typing:
        // - ends with a decimal point (e.g., "1.")
        // - is just a minus sign (e.g., "-")
        const isIncompleteNumber = trimmedValue.endsWith('.') || trimmedValue === '-';
        if (isIncompleteNumber) {
            return;
        }

        const numericValue = Number(trimmedValue.replace(/_/g, ''));
        if (Number.isFinite(numericValue)) {
            lastSentValue.current = numericValue;
            onValueChange(numericValue);
        }
    });

    return { displayValue, handleChange, setDisplayValue };
}
